import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Camera, Bike, Clock, ChevronRight, CheckCircle,
  LogOut, Eye, X, RefreshCw, CalendarDays, School, AlertCircle, Package,
} from "lucide-react";
import { getDeliveryBoyDetailsService, getPickupsSummary } from "@/services/deliveryAgent";
import { startLocationTracking, stopLocationTracking, isLocationTracking } from "@/lib/locationTracker";
import { getNotifications } from "@/services/parent";
import { storage } from "@/lib/storage";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import { colors } from "@/theme";
import NotificationsDropdown from "@/components/notifications/NotificationsDropdown";
import { useDispatch } from "react-redux";
import { logoutService } from "@/services/auth";
import { setRefreshTokenCookie } from "@/lib/cookie";
import { updateUserData } from "@/lib/store/slices/UserSlice";
import { clearPermissions } from "@/lib/store/slices/PermissionsSlice";
import toast from "react-hot-toast";

const API_BASE = (import.meta.env.VITE_BASE_URL || "https://mlb.meralunchbox.in/api").replace(/\/api$/, "");

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

export default function DeliveryDashboard() {
  const navigate = useNavigate();
  const userDetails = useCurrentUser();
  const userData = storage.getUserData();
  const username = userDetails?.username || userData?.user?.username || userData?.username || "";
  const displayName = username ? username.charAt(0).toUpperCase() + username.slice(1).toLowerCase() : "";

  const branchCode = userDetails?.branch_code || userData?.branch_code || "";

  const { canView: canViewNotifications } = useScreenPermission("notifications");
  const { canView: canViewHolidays } = useScreenPermission("holidays");
  const [hasShift, setHasShift] = useState(false);
  const [loginImageUrl, setLoginImageUrl] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pickupSummary, setPickupSummary] = useState<{
    Total_Deliveries: number;
    Total_Parents: number;
    Total_Schools: number;
    Pending_Deliveries: number;
    Completed_Deliveries: number;
    InTransit_Deliveries: number;
    Deliveries_Per_School: Record<string, number>;
  } | null>(null);
  const dispatch = useDispatch();
  let isLoggingOut = false;

  const checkShiftStatus = async (quiet = false) => {
    if (!username) return;
    if (!quiet) setLoading(true);
    try {
      const { data } = await getDeliveryBoyDetailsService(username);
      const record = Array.isArray(data) ? (data as any[])[0] : (data as any);
      const active = !!(record?.id);
      setHasShift(active);
      // Resume location tracking if shift is active but tracker was lost (e.g. page refresh)
      if (active && username && !isLocationTracking()) {
        startLocationTracking(userDetails);
      }
      if (active && record?.Image) {
        const p = record.Image as string;
        setLoginImageUrl(p.startsWith("http") ? p : `${API_BASE}${p}`);
      } else {
        setLoginImageUrl("");
      }
      if (active && branchCode) {
        const summaryRes = await getPickupsSummary();
        setPickupSummary((summaryRes.data as any)?.Summary ?? null);
      } else {
        setPickupSummary(null);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { checkShiftStatus(); }, [username]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await checkShiftStatus(true);
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
      const refreshToken = storage.getRefreshToken();

      if (refreshToken) {
        stopLocationTracking();
        await logoutService(refreshToken);
      }
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      storage.setRefreshToken('');
      setRefreshTokenCookie('');
      storage.logout();
      dispatch(updateUserData(null));
      dispatch(clearPermissions());

      toast.success("Logged out successfully");
      window.location.href = "/#/login";;

      isLoggingOut = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Hero Banner ────────────────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden rounded-b-[32px] shadow-2xl px-6 pt-14 pb-6"
        style={{ background: colors.brand.gradient }}
      >
        <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-white/[0.06] pointer-events-none" />
        <div className="absolute -bottom-10 -left-10 w-36 h-36 rounded-full bg-white/[0.05] pointer-events-none" />

        {/* Top row */}
        <div className="flex items-center justify-between mb-5 relative z-10">
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center border border-white/25 shadow-lg text-white font-extrabold text-base"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              {displayName.charAt(0)}
            </div>
            <div>
              <p className="text-white/55 text-xs font-medium tracking-wide uppercase">Delivery Agent</p>
              <p className="text-white font-bold text-sm">{displayName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canViewNotifications && (
              <NotificationsDropdown
                fetchFn={() => getNotifications(username)}
                viewAllPath="/notifications"
              />
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleLogout}
              className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Greeting */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative z-10 mb-5"
        >
          <p className="text-white/55 text-sm">{getGreeting()},</p>
          <h1 className="text-white text-2xl font-extrabold tracking-tight">{displayName} 👋</h1>
        </motion.div>

        {/* Shift status pill */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="flex items-center gap-3 bg-white/10 border border-white/15 rounded-2xl px-4 py-3 relative z-10"
        >
          {loading ? (
            <div className="w-3 h-3 rounded-full border-2 border-white/40 border-t-white animate-spin" />
          ) : (
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${hasShift ? "bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.7)]" : "bg-red-400"}`} />
          )}
          <span className="text-white/85 text-sm font-semibold flex-1">
            {loading ? "Checking shift…" : hasShift ? "Shift Active" : "Shift Not Started"}
          </span>
          {!loading && !hasShift && (
            <button
              onClick={() => navigate("/delivery/shift-start")}
              className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white transition-transform hover:scale-105 active:scale-95"
              style={{ color: colors.brand.secondary }}
            >
              Start Now
            </button>
          )}
        </motion.div>

        {/* ── Shift photo box — hidden image, eye icon reveals it ─────────────── */}
        <AnimatePresence>
          {!loading && hasShift && loginImageUrl && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.25 }}
              className="flex items-center justify-between mt-4 rounded-2xl border border-white/20 bg-white/10 px-4 py-3 z-10 relative"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Camera className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white text-sm font-bold leading-tight">Today's shift photo</p>
                  <p className="text-white/50 text-xs">Tap eye icon to view</p>
                </div>
              </div>
              <button
                onClick={() => setImageModalOpen(true)}
                className="w-9 h-9 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center text-white hover:bg-white/25 transition-colors flex-shrink-0"
              >
                <Eye className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Action Cards ─────────────────────────────────────────────────────────── */}
      <div className="px-5 pt-6 space-y-4 pb-10">
        {/* Start shift card */}
        {!loading && !hasShift && (
          <motion.button
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22 }}
            onClick={() => navigate("/delivery/shift-start")}
            className="w-full flex items-center gap-4 p-5 rounded-2xl shadow-lg transition-transform hover:scale-[1.01] active:scale-[0.99] text-left"
            style={{ background: colors.brand.gradient }}
          >
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Camera className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-base leading-tight">Start Your Shift</p>
              <p className="text-white/60 text-xs mt-0.5">Take a selfie to begin today's duty</p>
            </div>
            <ChevronRight className="w-5 h-5 text-white/60" />
          </motion.button>
        )}

        {/* Today's Pickups card */}
        {!loading && hasShift && (
          <motion.button
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            onClick={() => navigate("/delivery/pickups")}
            className="w-full flex items-center gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all hover:scale-[1.01] active:scale-[0.99] text-left"
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: `${colors.brand.primary}18` }}
            >
              <Bike className="w-6 h-6" style={{ color: colors.brand.primary }} />
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900 text-base leading-tight">Today's Pickups</p>
              <p className="text-gray-500 text-xs mt-0.5">View schedule & confirm deliveries</p>
            </div>
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${colors.brand.primary}15` }}
            >
              <ChevronRight className="w-4 h-4" style={{ color: colors.brand.primary }} />
            </div>
          </motion.button>
        )}

        {/* Holidays */}
        {canViewHolidays && (<motion.button
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={() => navigate("/delivery/holidays")}
          className="w-full flex items-center gap-4 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all hover:scale-[1.01] active:scale-[0.99] text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-6 h-6 text-brand-500" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-gray-900 text-base leading-tight">Holidays</p>
            <p className="text-gray-500 text-xs mt-0.5">View public holidays &amp; festivals</p>
          </div>
          <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
            <ChevronRight className="w-4 h-4 text-brand-500" />
          </div>
        </motion.button>)}

        {/* Pickups Summary */}
        {!loading && hasShift && pickupSummary && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.32 }}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <Bike className="w-4 h-4" style={{ color: colors.brand.primary }} />
              <p className="font-bold text-gray-800 text-sm">Today's Pickup Summary</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-gray-100">
              {[
                { label: "Total", value: pickupSummary.Total_Deliveries, icon: <Package className="w-3.5 h-3.5" />, color: "text-gray-700" },
                { label: "Completed", value: pickupSummary.Completed_Deliveries, icon: <CheckCircle className="w-3.5 h-3.5" />, color: "text-green-600" },
                { label: "In Transit", value: pickupSummary.InTransit_Deliveries, icon: <Bike className="w-3.5 h-3.5" />, color: "text-amber-600" },
                { label: "Pending", value: pickupSummary.Pending_Deliveries, icon: <AlertCircle className="w-3.5 h-3.5" />, color: "text-red-500" },
                { label: "Parents", value: pickupSummary.Total_Parents, icon: <ChevronRight className="w-3.5 h-3.5" />, color: "text-brand-500" },
                { label: "Schools", value: pickupSummary.Total_Schools, icon: <School className="w-3.5 h-3.5" />, color: "text-purple-600" },
              ].map((s) => (
                <div key={s.label} className="bg-white px-3 py-3 text-center">
                  <p className={`text-xl font-extrabold ${s.color}`}>{s.value}</p>
                  <div className={`flex items-center justify-center gap-1 mt-0.5 ${s.color}`}>
                    {s.icon}
                    <p className="text-xs font-medium text-gray-500">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>
            {Object.keys(pickupSummary.Deliveries_Per_School).length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-500 mb-2">Per School</p>
                <div className="space-y-1.5">
                  {Object.entries(pickupSummary.Deliveries_Per_School).map(([school, count]) => (
                    <div key={school} className="flex items-center justify-between">
                      <span className="text-xs text-gray-700 truncate flex-1">{school}</span>
                      <span className="text-xs font-bold ml-2 px-2 py-0.5 rounded-full bg-brand-50 text-brand-700">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.33 }}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4"
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${colors.brand.primary}15` }}>
              <Clock className="w-5 h-5" style={{ color: colors.brand.primary }} />
            </div>
            <p className="text-gray-500 text-xs font-medium mb-0.5">Today's Date</p>
            <p className="text-gray-900 font-bold text-sm">
              {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38 }}
            className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4"
          >
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3 bg-green-50">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-gray-500 text-xs font-medium mb-0.5">Shift Status</p>
            <p className={`font-bold text-sm ${hasShift ? "text-green-600" : "text-red-500"}`}>
              {loading ? "Checking…" : hasShift ? "Active" : "Not Started"}
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Full-image modal ───────────────────────────────────────────────────── */}
      <AnimatePresence>
        {imageModalOpen && loginImageUrl && (
          <motion.div
            key="shift-img-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center px-4"
            onClick={() => setImageModalOpen(false)}
          >
            <button
              onClick={() => setImageModalOpen(false)}
              className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center text-white hover:bg-white/25 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <motion.img
              initial={{ scale: 0.88, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={loginImageUrl}
              alt="Shift selfie"
              className="max-w-sm w-full rounded-2xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
